/**
 * DOM level guard that switches browser and password manager autofill off for
 * every text like field, whichever library rendered it.
 *
 * Fields opt back in by carrying `data-autofill="allow"` (or by living under an
 * element that does), e.g. the sign in pages. The marker may be added or removed
 * at runtime; fields are guarded or restored accordingly.
 *
 * Limitation: shadow roots and iframes are not observed.
 */

const ALLOW_SELECTOR = '[data-autofill="allow"]';

/** Value written to every non password field. Swap for a non standard token if
 * Chrome still offers email or address suggestions with "off". */
const OFF_TOKEN = "off";
const PASSWORD_TOKEN = "new-password";

const SKIPPED_INPUT_TYPES = new Set([
  "hidden",
  "file",
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "image",
  "range",
  "color",
]);

const PASSWORD_MANAGER_ATTRIBUTES: Record<string, string> = {
  "data-1p-ignore": "true",
  "data-lpignore": "true",
  "data-bwignore": "true",
  "data-form-type": "other",
};

const FIELD_SELECTOR = "input, textarea";

interface GuardRecord {
  /** Attribute values the field had before the guard first touched it. */
  original: Record<string, string | null>;
  /** `autocomplete` value the guard last wrote, null if it never wrote one. */
  writtenAutocomplete: string | null;
}

/** Fields the guard has touched, so one that later becomes allowed can be
 * restored without clobbering values the app set in the meantime. */
const guarded = new WeakMap<Element, GuardRecord>();

const setAttributeIfChanged = (el: Element, name: string, value: string) => {
  if (el.getAttribute(name) !== value) el.setAttribute(name, value);
};

const guardField = (el: Element) => {
  if (el.closest(ALLOW_SELECTOR)) return;

  const isTextarea = el.tagName === "TEXTAREA";
  const type = isTextarea
    ? "text"
    : (el.getAttribute("type") || "text").toLowerCase();
  if (!isTextarea && SKIPPED_INPUT_TYPES.has(type)) return;

  let record = guarded.get(el);
  if (!record) {
    record = { original: {}, writtenAutocomplete: null };
    for (const name of [
      "autocomplete",
      ...Object.keys(PASSWORD_MANAGER_ATTRIBUTES),
    ]) {
      record.original[name] = el.getAttribute(name);
    }
    guarded.set(el, record);
  }

  const current = (el.getAttribute("autocomplete") || "").trim().toLowerCase();
  const replaceable = current === "" || current === "on" || current === "off";

  if (replaceable) {
    // "off" is ignored by Chrome on password fields, so even an explicit "off"
    // is upgraded. Other explicit tokens (current-password, ...) are kept.
    const token = type === "password" ? PASSWORD_TOKEN : OFF_TOKEN;
    setAttributeIfChanged(el, "autocomplete", token);
    record.writtenAutocomplete = token;
  }

  for (const [name, value] of Object.entries(PASSWORD_MANAGER_ATTRIBUTES)) {
    setAttributeIfChanged(el, name, value);
  }
};

/** Undoes the guard on a field that has become allowed (e.g. a container's
 * `data-autofill="allow"` was set after the field was already guarded). */
const releaseField = (el: Element) => {
  const record = guarded.get(el);
  if (!record || !el.closest(ALLOW_SELECTOR)) return;
  guarded.delete(el);

  const restore = (name: string) => {
    const original = record.original[name];
    if (original === null) el.removeAttribute(name);
    else el.setAttribute(name, original);
  };

  // Only undo what the guard wrote; a value the app set since is left alone.
  if (
    record.writtenAutocomplete !== null &&
    el.getAttribute("autocomplete") === record.writtenAutocomplete
  ) {
    restore("autocomplete");
  }
  for (const name of Object.keys(PASSWORD_MANAGER_ATTRIBUTES)) restore(name);
};

const syncField = (el: Element) => {
  releaseField(el);
  guardField(el);
};

const syncTree = (node: Node) => {
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  if (el.matches(FIELD_SELECTOR)) syncField(el);
  el.querySelectorAll(FIELD_SELECTOR).forEach(syncField);
};

/**
 * Guards every current and future field under `root`. Returns a function that
 * stops the guard.
 */
export const installAutofillGuard = (
  root: Element = document.documentElement
): (() => void) => {
  syncTree(root);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "childList") {
        mutation.addedNodes.forEach(syncTree);
      } else if (mutation.attributeName === "data-autofill") {
        syncTree(mutation.target);
      } else if (mutation.target.nodeType === Node.ELEMENT_NODE) {
        const el = mutation.target as Element;
        if (el.matches(FIELD_SELECTOR)) guardField(el);
      }
    }
  });

  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["type", "autocomplete", "data-autofill"],
  });

  return () => observer.disconnect();
};
